import type { FC, ReactNode, FormEvent } from 'react'

import { useState } from 'react'

import { delay } from '@/lib/ui'

type Status = 'idle' | 'sending' | 'sent' | 'error'

const TEXT_FIELDS = [
    { name: 'name', label: 'Your name', index: '01', type: 'text', autoComplete: 'name', max: 120 },
    { name: 'email', label: 'Email address', index: '02', type: 'email', autoComplete: 'email', max: 180 },
    { name: 'project', label: 'Project', index: '03', type: 'text', autoComplete: 'organization', max: 180 }
]

const MESSAGE_MAX = 4000

const BUTTON_LABEL: Record<Status, string> = {
    idle: 'Send enquiry',
    sending: 'Sending',
    sent: 'Sent',
    error: 'Try again'
}

export const ContactForm: FC = (): ReactNode => {
    const [status, setStatus] = useState<Status>('idle')
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [failure, setFailure] = useState('')
    const [messageLength, setMessageLength] = useState(0)

    const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (status === 'sending') return

        const form = event.currentTarget
        const data = new FormData(form)

        const payload = {
            name: String(data.get('name') ?? ''),
            email: String(data.get('email') ?? ''),
            project: String(data.get('project') ?? ''),
            message: String(data.get('message') ?? '')
        }

        setStatus('sending')
        setErrors({})
        setFailure('')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            })

            const result = await response.json().catch(() => null)

            if (!response.ok || !result?.ok) {
                setErrors(result?.errors ?? {})
                setFailure(result?.errors ? '' : (result?.message ?? 'Something went wrong. Please try again.'))
                setStatus('error')

                return
            }

            form.reset()
            setMessageLength(0)
            setStatus('sent')
        } catch {
            setFailure('We could not reach the studio just now. Please try again, or email us directly.')
            setStatus('error')
        }
    }

    if (status === 'sent') {
        return (
            <div
                className='animate-in fade-in slide-in-from-bottom-4 border border-line-soft p-[clamp(28px,4vw,56px)] duration-700 ease-out'
                role='status'
            >
                <div className='ed-mono text-faint'>
                    Received
                </div>

                <p className='ed-lead mt-[clamp(20px,2.6vw,34px)] max-w-[36ch]'>
                    Thank you. Your enquiry is with the studio and you will hear back within two working days.
                </p>

                <button
                    type='button'
                    className='fm-submit mt-[clamp(28px,3.6vw,48px)]'
                    onClick={() => setStatus('idle')}
                >
                    <span>
                        Send another
                    </span>
                </button>
            </div>
        )
    }

    return (
        <form onSubmit={onSubmit} noValidate>
            <div className='flex flex-col gap-[clamp(26px,3.4vw,44px)]'>
                {TEXT_FIELDS.map((field, index) => (
                    <div key={field.name} data-reveal='up' style={delay(index * 0.06)}>
                        <div className='fm-field' data-invalid={Boolean(errors[field.name])}>
                            <input
                                id={field.name}
                                name={field.name}
                                type={field.type}
                                className='fm-input'
                                placeholder=' '
                                autoComplete={field.autoComplete}
                                maxLength={field.max}
                                aria-invalid={Boolean(errors[field.name])}
                                aria-describedby={errors[field.name] ? `${field.name}-error` : undefined}
                            />

                            <span className='fm-line' aria-hidden />

                            <label className='fm-label' htmlFor={field.name}>
                                {field.label}
                            </label>

                            <span className='fm-index' aria-hidden>
                                {field.index}
                            </span>
                        </div>

                        {errors[field.name] && (
                            <p className='fm-error' id={`${field.name}-error`}>
                                {errors[field.name]}
                            </p>
                        )}
                    </div>
                ))}

                <div data-reveal='up' style={delay(0.18)}>
                    <div className='fm-field' data-invalid={Boolean(errors.message)}>
                        <textarea
                            id='message'
                            name='message'
                            className='fm-input'
                            placeholder=' '
                            rows={5}
                            maxLength={MESSAGE_MAX}
                            aria-invalid={Boolean(errors.message)}
                            aria-describedby={errors.message ? 'message-error' : undefined}
                            onChange={(event) => setMessageLength(event.currentTarget.value.length)}
                        />

                        <span className='fm-line' aria-hidden />

                        <label className='fm-label' htmlFor='message'>
                            Message
                        </label>

                        <span className='fm-index' aria-hidden>
                            04
                        </span>
                    </div>

                    <div className='mt-3 flex items-center justify-between gap-6'>
                        {errors.message
                            ? (
                                <p className='fm-error' id='message-error'>
                                    {errors.message}
                                </p>
                            )
                            : <span />}

                        <span className={`ed-mono transition-colors duration-500 ${messageLength > 0 ? 'text-muted-foreground' : 'text-faint'}`}>
                            {messageLength} / {MESSAGE_MAX}
                        </span>
                    </div>
                </div>
            </div>

            {failure && (
                <p className='fm-error mt-8' role='alert'>
                    {failure}
                </p>
            )}

            <div
                className='mt-[clamp(36px,5vw,64px)] flex flex-wrap items-center justify-between gap-6'
                data-reveal='up'
                style={delay(0.24)}
            >
                <button type='submit' className='fm-submit' disabled={status === 'sending'}>
                    <span>
                        {BUTTON_LABEL[status]}
                    </span>
                </button>

                <span className='ed-mono text-faint'>
                    Answered within two working days
                </span>
            </div>
        </form>
    )
}
